import { XMLParser } from "fast-xml-parser";
import XMLBuilder from "fast-xml-builder";
import {
  coerceToBoolean,
  lookupIdToName,
  lookupNameToId,
  updateDeviceParamsFormat,
} from "./Helpers";

/**
 * Converts one `WriteTagConfigRequest` element from hub tag XML into a grid tag.
 * @param {object} tag The element's attributes, as parsed.
 * @param {object[]} lookupValues Lookup value categories from LookupValueContext.
 * @returns {object}
 */
export const xmlTagToGridTag = (tag, lookupValues) => {
  let convertedTag = { SyncToHub: true, ...tag };
  // Field mappings
  // Type -> TagType
  // TagID -> TagIdentifier
  // DisplayName -> TagName
  // DataType -> TagDataType
  convertedTag.TagType = convertedTag.Type;
  convertedTag.TagIdentifier = convertedTag.TagID;
  convertedTag.TagName = convertedTag.DisplayName;
  convertedTag.IsActive = coerceToBoolean(convertedTag.IsEnabled);
  convertedTag.IsWriteable = coerceToBoolean(convertedTag.IsWriteable);
  convertedTag.AlarmTypeId = lookupNameToId(
    convertedTag.AlarmType,
    "TagAlarmType",
    lookupValues
  );
  convertedTag.Alarm2TypeId = lookupNameToId(
    convertedTag.Alarm2Type,
    "TagAlarmType",
    lookupValues
  );
  convertedTag.TagSourceTypeId = lookupNameToId(
    convertedTag.SourceType,
    "TagSourceType",
    lookupValues
  );
  convertedTag.TagTypeId = lookupNameToId(
    convertedTag.TagType,
    "TagType",
    lookupValues
  );
  convertedTag.DataTypeId = lookupNameToId(
    convertedTag.DataType,
    "TagDataType",
    lookupValues
  );
  convertedTag.AlarmArmingTypeId = lookupNameToId(
    convertedTag.AlarmArmingType,
    "AlarmArmingType",
    lookupValues
  );
  convertedTag.AlarmSeverityId = lookupNameToId(
    convertedTag.AlarmPriority,
    "AlarmSeverity",
    lookupValues
  );

  // Check DeviceParams format and adjust if needed
  convertedTag.DeviceParams = updateDeviceParamsFormat(
    convertedTag.DeviceParams,
    convertedTag.SourceType,
    convertedTag.ChannelNumber
  );
  delete convertedTag.Type;
  delete convertedTag.TagID;
  delete convertedTag.DisplayName;
  delete convertedTag.IsEnabled;
  return convertedTag;
};

/**
 * Parses a hub tag XML document into grid tags.
 * @param {string} xmlString One or more `WriteTagConfigRequest` elements.
 * @param {object[]} lookupValues Lookup value categories from LookupValueContext.
 * @returns {object[]}
 * @throws {Error} When the document has no `WriteTagConfigRequest` element.
 */
export const parseTagsXml = (xmlString, lookupValues) => {
  const parser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: "",
  });
  const parsedXML = parser.parse(xmlString);
  if (!parsedXML.WriteTagConfigRequest) {
    throw new Error("Incorrect XML format");
  }
  if (Array.isArray(parsedXML.WriteTagConfigRequest)) {
    return parsedXML.WriteTagConfigRequest.map((tag) =>
      xmlTagToGridTag(tag, lookupValues)
    );
  }
  return [xmlTagToGridTag(parsedXML.WriteTagConfigRequest, lookupValues)];
};

/**
 * Converts a grid tag into the attributes of a `WriteTagConfigRequest` element, dropping fields
 * that tie it to a saved tag, asset or tag list.
 * @param {object} tag A grid tag.
 * @param {object[]} lookupValues Lookup value categories from LookupValueContext.
 * @returns {object}
 */
export const gridTagToXmlTag = (tag, lookupValues) => {
  let xmlTag = { ...tag };

  if (xmlTag.TagType !== undefined) {
    xmlTag.Type = xmlTag.TagType;
  }
  if (xmlTag.TagIdentifier !== undefined) {
    xmlTag.TagID = xmlTag.TagIdentifier;
  }
  if (xmlTag.TagName !== undefined) {
    xmlTag.DisplayName = xmlTag.TagName;
  }
  if (xmlTag.IsActive !== undefined) {
    xmlTag.IsEnabled = coerceToBoolean(xmlTag.IsActive);
  }
  if (xmlTag.IsWriteable !== undefined) {
    xmlTag.IsWriteable = coerceToBoolean(xmlTag.IsWriteable);
  }

  if (xmlTag.AlarmTypeId) {
    try {
      xmlTag.AlarmType = lookupIdToName(
        xmlTag.AlarmTypeId,
        "TagAlarmType",
        lookupValues
      );
    } catch (e) {
      console.warn(
        "Could not convert AlarmTypeId to name:",
        xmlTag.AlarmTypeId
      );
    }
    delete xmlTag.AlarmTypeId;
  }
  if (xmlTag.Alarm2TypeId) {
    try {
      xmlTag.Alarm2Type = lookupIdToName(
        xmlTag.Alarm2TypeId,
        "TagAlarmType",
        lookupValues
      );
    } catch (e) {
      console.warn(
        "Could not convert Alarm2TypeId to name:",
        xmlTag.Alarm2TypeId
      );
    }
    delete xmlTag.Alarm2TypeId;
  }
  if (xmlTag.SourceTypeId) {
    try {
      xmlTag.SourceType = lookupIdToName(
        xmlTag.SourceTypeId,
        "TagSourceType",
        lookupValues
      );
    } catch (e) {
      console.warn(
        "Could not convert SourceTypeId to name:",
        xmlTag.SourceTypeId
      );
    }
    delete xmlTag.SourceTypeId;
  }
  if (xmlTag.TagSourceTypeId) {
    try {
      xmlTag.SourceType = lookupIdToName(
        xmlTag.TagSourceTypeId,
        "TagSourceType",
        lookupValues
      );
    } catch (e) {
      console.warn(
        "Could not convert TagSourceTypeId to name:",
        xmlTag.TagSourceTypeId
      );
    }
    delete xmlTag.TagSourceTypeId;
  }
  if (xmlTag.TagTypeId) {
    try {
      xmlTag.Type = lookupIdToName(xmlTag.TagTypeId, "TagType", lookupValues);
    } catch (e) {
      console.warn("Could not convert TagTypeId to name:", xmlTag.TagTypeId);
    }
    delete xmlTag.TagTypeId;
  }
  if (xmlTag.DataTypeId) {
    try {
      xmlTag.DataType = lookupIdToName(
        xmlTag.DataTypeId,
        "TagDataType",
        lookupValues
      );
    } catch (e) {
      console.warn("Could not convert DataTypeId to name:", xmlTag.DataTypeId);
    }
    delete xmlTag.DataTypeId;
  }

  delete xmlTag.TagType;
  delete xmlTag.TagIdentifier;
  delete xmlTag.TagName;
  delete xmlTag.IsActive;
  delete xmlTag.index;
  delete xmlTag.id;
  delete xmlTag.TagId;
  delete xmlTag.TCTagLayerId;
  delete xmlTag.AssetId;
  delete xmlTag.AssetNumber;
  delete xmlTag.AssetTagId;
  delete xmlTag.TagListName;

  return xmlTag;
};

/**
 * Builds a hub tag XML document from grid tags: one `WriteTagConfigRequest` element per tag.
 * @param {object[]} tags Grid tags.
 * @param {object[]} lookupValues Lookup value categories from LookupValueContext.
 * @returns {string}
 */
export const buildTagsXml = (tags, lookupValues) => {
  const xmlTags = tags.map((tag) => gridTagToXmlTag(tag, lookupValues));

  const xmlData = {
    WriteTagConfigRequest: xmlTags.length === 1 ? xmlTags[0] : xmlTags,
  };

  const builder = new XMLBuilder({
    ignoreAttributes: false,
    attributeNamePrefix: "",
    format: true,
    indentBy: "  ",
    suppressBooleanAttributes: false,
  });

  return builder.build(xmlData);
};
